import { useEffect, useMemo } from 'react';
import { useQuery, useLazyQuery, skipToken } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { DateTime } from 'luxon';

import HEADER_IMAGE from 'assets/images/academy-management-header.webp';
import { PageDrawer } from 'common/components/PageDrawer';
import { useHashMatch } from 'route/hooks/useHashMatch';
import { Divider } from 'common/components/Divider';
import {
    IMainMenu,
    MenuItemsDocument,
    OrganisationPlanSubscriptionDocument,
    CurrentUserDocument,
    ChildOrganisationsDocument,
    IRole,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSwitchOrganisation } from 'organisation/hooks/useSwitchOrganisation';
import { MAIN_MENU } from 'common/constants/mainMenu';
import { Loader } from 'common/components/Loader';
import { ILinkProps, Link } from 'common/components/Link';
import { DrawerHeaderImage } from 'common/components/DrawerHeaderImage';

const subItemLinkStyle: SxProps = {
    ':hover': {
        color: 'text.secondary',
    },
};

export const AcademyManagementDrawer = () => {
    const [translate] = useTranslation();
    const isManagementPage = useHashMatch('MANAGEMENT_ACADEMY');
    const { showApolloError } = useApolloError();
    const { switchOrganisation } = useSwitchOrganisation();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        data: menuItemsData,
        loading: menuItemsLoading,
        error: menuItemsError,
    } = useQuery(MenuItemsDocument, isManagementPage ? {} : skipToken);

    useEffect(() => {
        if (menuItemsError) {
            showApolloError(menuItemsError);
        }
    }, [menuItemsError, showApolloError]);
    const [
        getOrganisationPlanSubscription,
        {
            data: organisationPlanSubscriptionData,
            loading: organisationPlanSubscriptionLoading,
        },
    ] = useLazyQuery(OrganisationPlanSubscriptionDocument);
    const { data: currentUserData, loading: loadingUser } = useQuery(
        CurrentUserDocument,
        isManagementPage ? {} : skipToken
    );
    const isGhostUser = !!currentUserData?.currentUser?.sourceOrganisation;
    const { data: childOrganisationsData, loading: childOrganisationsLoading } =
        useQuery(
            ChildOrganisationsDocument,
            isManagementPage && !isGhostUser && !loadingUser ? {} : skipToken
        );

    const { organisation } = organisationPlanSubscriptionData || {};
    const { menuItems } = menuItemsData || {};
    const { currentUser } = currentUserData || {};
    const { roles } = currentUser || {};
    const childOrganisations = isGhostUser
        ? undefined
        : childOrganisationsData?.childOrganisations;

    const canSeePlanTrialExpiredPage = useMemo(
        () => roles?.includes(IRole.Owner) || roles?.includes(IRole.Manager),
        [roles]
    );

    const managementMenu = menuItems?.find(
        (item) => item.menu === IMainMenu.Management
    );

    // Move skills management item to the modules section
    const subMenus = useMemo(() => {
        if (!managementMenu?.subMenus) return managementMenu?.subMenus;

        const skillsItem = managementMenu.subMenus
            .find((item) => item?.menu === 'MANAGEMENT_SETTINGS')
            ?.subMenus?.find(
                (item) => item?.menu === 'MANAGEMENT_SETTINGS_SKILLS'
            );

        if (!skillsItem) return managementMenu.subMenus;

        return managementMenu.subMenus.map((section) => {
            if (section?.menu === 'MANAGEMENT_MODULES') {
                const certificatesIndex = (section.subMenus ?? []).findIndex(
                    (item) => item?.menu === 'MANAGEMENT_MODULES_CERTIFICATES'
                );

                if (certificatesIndex === -1) return section;

                return {
                    ...section,
                    subMenus: [
                        ...section.subMenus!.slice(0, certificatesIndex + 1),
                        skillsItem,
                        ...section.subMenus!.slice(certificatesIndex + 1),
                    ],
                };
            }

            if (section?.menu === 'MANAGEMENT_SETTINGS') {
                return {
                    ...section,
                    subMenus: section.subMenus?.filter(
                        (item) => item?.menu !== 'MANAGEMENT_SETTINGS_SKILLS'
                    ),
                };
            }

            return section;
        });
    }, [managementMenu?.subMenus]);

    const { trialExpires } = organisation || {};

    const trialPeriodDays = useMemo(() => {
        if (!canSeePlanTrialExpiredPage || !trialExpires) return;

        const dateNow = DateTime.now();
        const trialExpiryDate = DateTime.fromISO(trialExpires);
        const dayDifference = trialExpiryDate.diff(dateNow, 'days').toObject();

        if (dayDifference.days === undefined) return;

        // Return the number of days left, make sure it's at least 0
        return Math.max(0, Math.ceil(dayDifference.days));
    }, [canSeePlanTrialExpiredPage, trialExpires]);

    useEffect(() => {
        if (!isManagementPage || !canSeePlanTrialExpiredPage) {
            return;
        }

        getOrganisationPlanSubscription();
    }, [
        isManagementPage,
        canSeePlanTrialExpiredPage,
        getOrganisationPlanSubscription,
    ]);

    const handleClose = () => {
        // Close the drawer by redirecting to the same page without the hashtag
        navigate(`${location.pathname}${location.search}`);
    };

    const isLoading =
        menuItemsLoading ||
        loadingUser ||
        organisationPlanSubscriptionLoading ||
        childOrganisationsLoading;

    return (
        <PageDrawer open={isManagementPage} onClose={handleClose}>
            <DrawerHeaderImage src={HEADER_IMAGE} />
            {isLoading && <Loader />}
            {!isLoading && (
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        sx={{
                            alignItems: { xs: 'flex-start', sm: 'flex-end' },
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h2">
                            {translate('academyManagement')}
                        </Typography>
                        {trialPeriodDays !== undefined && (
                            <Typography
                                color="error.main"
                                fontWeight={700}
                                variant="body2"
                            >
                                {translate('planSubscriptionExpiryDays', {
                                    count: trialPeriodDays,
                                })}
                            </Typography>
                        )}
                    </Stack>

                    <Divider sx={{ mt: 2, mb: 3 }} />

                    <Grid
                        container
                        rowSpacing={4}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fit, minmax(170px, 1fr))',
                            gridTemplateRows: 'auto 1fr',
                        }}
                    >
                        {subMenus?.map((item) => {
                            if (!item) return null;

                            const { customText } = item;

                            const menuData = MAIN_MENU[item.menu];

                            let text: string | null = customText || null;

                            if (menuData?.translation) {
                                text = translate(menuData.translation);
                            }

                            return (
                                <Grid key={item.menu}>
                                    <Typography
                                        sx={{ fontWeight: 700 }}
                                        variant="body1"
                                    >
                                        {text}
                                    </Typography>
                                    <Box sx={{ overflow: 'visible' }}>
                                        {item.subMenus?.map((subItem) => {
                                            if (!subItem) return null;

                                            const subMenuData =
                                                MAIN_MENU[subItem.menu];

                                            if (!subMenuData?.translation) {
                                                return;
                                            }

                                            const linkProps: ILinkProps = {
                                                href: subMenuData.href,
                                                to: subMenuData.url,
                                                underline: 'none',
                                                sx: subItemLinkStyle,
                                            };

                                            if (subMenuData.component) {
                                                linkProps.component =
                                                    subMenuData.component;
                                            }

                                            return (
                                                <Box
                                                    key={subItem.menu}
                                                    sx={{ overflow: 'visible' }}
                                                >
                                                    <Link
                                                        {...linkProps}
                                                        key={subItem.menu}
                                                    >
                                                        {translate(
                                                            subMenuData.translation
                                                        )}
                                                    </Link>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Grid>
                            );
                        })}
                        {!!childOrganisations?.length && (
                            <Grid key="child-academies">
                                <Typography
                                    sx={{ fontWeight: 700 }}
                                    variant="body1"
                                >
                                    {translate('managementChildAcademies')}
                                </Typography>
                                {childOrganisations.map((org) => (
                                    <Link
                                        key={org.id}
                                        sx={{
                                            ...subItemLinkStyle,
                                            cursor: 'pointer',
                                            display: 'block',
                                            maxWidth: 'fit-content',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        underline="none"
                                        onClick={() =>
                                            switchOrganisation(org.id)
                                        }
                                    >
                                        {org.name}
                                    </Link>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}
        </PageDrawer>
    );
};
